import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { matchPrefix, useRouter, Link } from "@/app/router";
import { useSession } from "@/app/session";
import { Button, Card, Spinner } from "@/components/ui";
import { useToast, errorMessage } from "@/components/Toast";

interface Preview { workspace_name: string; email: string; role: string; valid: boolean }

export function InvitationPage() {
  const { path, navigate } = useRouter();
  const token = matchPrefix(path, "invitations") ?? "";
  const { me, refresh } = useSession();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["invite-preview", token],
    queryFn: () => api.get<Preview>(`/invitations/${token}`),
    retry: false,
  });

  async function accept() {
    setBusy(true);
    try {
      await api.post(`/invitations/${token}/accept`);
      await refresh();
      navigate("/tracker", { replace: true });
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2.5">
          <span className="size-3 rounded-full bg-running" />
          <span className="text-lg font-semibold tracking-tight">Timer</span>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : !data || !data.valid ? (
          <Card className="p-6 text-center">
            <p className="text-sm text-muted">This invitation has expired or is no longer valid.</p>
            <Link to="/login" className="mt-4 inline-block text-sm font-medium text-accent hover:underline">Go to sign in</Link>
          </Card>
        ) : (
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Join {data.workspace_name}</h1>
            <p className="mt-1 mb-6 text-sm text-muted">You've been invited as {data.role} · {data.email}</p>
            {me ? (
              me.user.email.toLowerCase() === data.email.toLowerCase() ? (
                <Button onClick={accept} loading={busy} className="w-full">Accept invitation</Button>
              ) : (
                <Card className="p-4 text-sm text-muted">
                  You're signed in as {me.user.email}. Sign in as {data.email} to accept.
                </Card>
              )
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted">Sign in or create an account with <strong className="text-ink">{data.email}</strong> to accept.</p>
                <div className="flex gap-2">
                  <Link to="/login" className="flex-1"><Button variant="secondary" className="w-full">Sign in</Button></Link>
                  <Link to="/register" className="flex-1"><Button className="w-full">Create account</Button></Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
